export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

interface ValidationOptions {
  maxLength?: number;
  maxLines?: number;
  allowAsync?: boolean;
  enforceObjectReturn?: boolean;
}

// Dangerous patterns that should be blocked
const DANGEROUS_PATTERNS = [
  // Direct dangerous calls
  { pattern: /\beval\s*\(/, message: 'eval() is not allowed' },
  { pattern: /\bnew\s+Function\s*\(/, message: 'Function constructor is not allowed' },
  { pattern: /\bimport\s*\(/, message: 'Dynamic imports are not allowed' },
  { pattern: /\brequire\s*\(/, message: 'require() is not allowed' },

  // DOM/Browser APIs
  {
    pattern: /\b(document|window|location|history|navigator)\b/,
    message: 'Browser APIs are not allowed',
  },
  { pattern: /\b(fetch|XMLHttpRequest|WebSocket)\b/, message: 'Network APIs are not allowed' },
  {
    pattern: /\b(localStorage|sessionStorage|indexedDB)\b/,
    message: 'Storage APIs are not allowed',
  },

  // Dangerous object access
  { pattern: /\.__proto__/, message: 'Prototype access is not allowed' },
  { pattern: /\.constructor\s*\(/, message: 'Constructor access is not allowed' },
  { pattern: /\[['"`]constructor['"`]\]/, message: 'Constructor access is not allowed' },
  { pattern: /\[['"`]__proto__['"`]\]/, message: 'Prototype access is not allowed' },

  // Global object access
  { pattern: /\bglobal(This)?\b/, message: 'Global object access is not allowed' },
  {
    pattern: /\bself\s*\.(?!(postMessage|addEventListener|onmessage))/,
    message: 'Worker self access is restricted',
  },

  // Process/system access
  { pattern: /\bprocess\b/, message: 'Process access is not allowed' },
  { pattern: /\b__dirname\b/, message: 'System paths are not allowed' },
  { pattern: /\b__filename\b/, message: 'System paths are not allowed' },
];

// Suspicious patterns that generate warnings
const SUSPICIOUS_PATTERNS = [
  {
    pattern: /\bsetTimeout\s*\(/,
    message: 'setTimeout detected - consider using async/await instead',
  },
  { pattern: /\bsetInterval\s*\(/, message: 'setInterval detected - may cause performance issues' },
  { pattern: /\bwhile\s*\(\s*true\s*\)/, message: 'Infinite loop detected' },
  { pattern: /\bfor\s*\(\s*;\s*;\s*\)/, message: 'Infinite loop detected' },
  { pattern: /\bthrow\b/, message: 'Exception throwing detected' },
];

const validateReturnType = (script: string): string[] => {
  const errors: string[] = [];

  // Check for return statements
  const returnMatches = script.match(/return\s+([^;]+)/g);

  if (!returnMatches || returnMatches.length === 0) {
    errors.push('Script must include at least one return statement');
    return errors;
  }
  // TODO: @theronburger fix your regex mate __ @theronburger wrote this on lucas computer ;)
  // // Analyze return statements for object-like patterns
  // const hasObjectReturn = returnMatches.some((returnStatement) => {
  //   const returnValue = returnStatement.replace(/^return\s+/, '').trim();

  //   // Check for object literal patterns
  //   const objectPatterns = [
  //     /^\{.*\}$/s, // Object literal: { ... }
  //     /^context(\.[a-zA-Z_]\w*)*$/, // context or context.property
  //     /^[a-zA-Z_]\w*\.(map|filter|reduce|slice)\s*\(/, // Array methods that return new arrays/objects
  //     /^Object\.(assign|create|keys|values|entries)/, // Object static methods
  //     /^\[.*\]$/s, // Array literal (arrays are objects in JS)
  //   ];

  //   return objectPatterns.some((pattern) => pattern.test(returnValue));
  // });

  // if (!hasObjectReturn) {
  //   errors.push(
  //     'Script must return an object or array. Use object literals {}, arrays [], or object/array methods.'
  //   );
  // }

  return errors;
};

export const validateScript = (
  script: string,
  options: ValidationOptions = {}
): ValidationResult => {
  const {
    maxLength = 10000,
    maxLines = 500,
    allowAsync = true,
    enforceObjectReturn = false,
  } = options;

  const errors: string[] = [];
  const warnings: string[] = [];

  // Basic validation
  if (!script || typeof script !== 'string') {
    errors.push('Script must be a non-empty string');
    return { isValid: false, errors, warnings };
  }

  // Size validation
  if (script.length > maxLength) {
    errors.push(`Script too long (${script.length} chars, max ${maxLength})`);
  }

  const lines = script.split('\n');
  if (lines.length > maxLines) {
    errors.push(`Script has too many lines (${lines.length}, max ${maxLines})`);
  }

  // Check for dangerous patterns - replace for-of with forEach
  DANGEROUS_PATTERNS.forEach(({ pattern, message }) => {
    if (pattern.test(script)) {
      errors.push(message);
    }
  });

  // Check for suspicious patterns - replace for-of with forEach
  SUSPICIOUS_PATTERNS.forEach(({ pattern, message }) => {
    if (pattern.test(script)) {
      warnings.push(message);
    }
  });

  // Async/await validation
  if (!allowAsync && /\b(async|await)\b/.test(script)) {
    errors.push('Async/await is not allowed in this context');
  }

  // Return type validation
  if (enforceObjectReturn) {
    const returnErrors = validateReturnType(script);
    errors.push(...returnErrors);
  }

  // Basic syntax check (handle with proper error typing)
  try {
    // We need this for syntax validation, but we'll add the ESLint disable
    // eslint-disable-next-line no-new-func, no-new, @typescript-eslint/no-implied-eval
    new Function('context', 'ui', script);
  } catch (error) {
    const syntaxError = error instanceof Error ? error : new Error(String(error));
    errors.push(`Syntax error: ${syntaxError.message}`);
  }

  // Check for balanced braces/brackets
  const braceCount = (script.match(/\{/g) || []).length - (script.match(/\}/g) || []).length;
  const bracketCount = (script.match(/\[/g) || []).length - (script.match(/\]/g) || []).length;
  const parenCount = (script.match(/\(/g) || []).length - (script.match(/\)/g) || []).length;

  if (braceCount !== 0) {
    errors.push('Unbalanced braces {}');
  }
  if (bracketCount !== 0) {
    errors.push('Unbalanced brackets []');
  }
  if (parenCount !== 0) {
    errors.push('Unbalanced parentheses ()');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

// Helper to create safe context with minimal attack surface
export const createSafeContext = (rawContext: any): any => {
  if (!rawContext || typeof rawContext !== 'object') {
    return {};
  }

  // Deep clone to prevent prototype pollution
  const safeContext = JSON.parse(JSON.stringify(rawContext));

  // Remove any function properties or dangerous objects
  const cleanContext = (obj: any): any => {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(cleanContext);
    }

    const cleaned: any = {};

    // Use Object.entries with filter and reduce instead of for-of with continue
    Object.entries(obj)
      .filter(([key]) => !['constructor', '__proto__', 'prototype'].includes(key))
      .filter(([, value]) => typeof value !== 'function')
      .forEach(([key, value]) => {
        cleaned[key] = cleanContext(value);
      });

    return cleaned;
  };

  return cleanContext(safeContext);
};
